import { CAMPAIGNS_ASSOCIATIONS_FAS_NAMESPACE } from './constants';
import { hasAssetGateAccess } from './utils';
const RPC_SOURCE = 'rpc';
function wrapValue(value) {
  return {
    source: RPC_SOURCE,
    isPortalSpecific: false,
    value
  };
}
function wrapOptionalValue(value) {
  if (value === undefined || value === null) {
    return undefined;
  }
  return wrapValue(value);
}
function transformAssetNameWithVariant(rpcValue) {
  return wrapValue({
    standaloneLabel: {
      singular: rpcValue.standaloneLabel.singular,
      plural: rpcValue.standaloneLabel.plural
    },
    inSentenceLabel: {
      singular: rpcValue.inSentenceLabel.singular,
      plural: rpcValue.inSentenceLabel.plural
    },
    variants: rpcValue.variants ? {
      propertyName: rpcValue.variants.propertyName,
      multipleItemsOnUI: rpcValue.variants.multipleItemsOnUI,
      options: (rpcValue.variants.options || []).map(option => ({
        value: option.value,
        standaloneLabel: {
          singular: option.standaloneLabel.singular,
          plural: option.standaloneLabel.plural
        },
        inSentenceLabel: {
          singular: option.inSentenceLabel.singular,
          plural: option.inSentenceLabel.plural
        }
      }))
    } : undefined
  });
}
function transformStatusProperty(rpcValue) {
  if (!rpcValue) {
    return undefined;
  }
  return wrapValue({
    name: rpcValue.name,
    statusDot: (rpcValue.statusDot || []).map(dot => {
      var _dot$hollow;
      return {
        name: dot.name,
        use: dot.use,
        hollow: (_dot$hollow = dot.hollow) !== null && _dot$hollow !== void 0 ? _dot$hollow : undefined,
        statusBucket: dot.statusBucket
      };
    })
  });
}
function transformPreviewProperty(rpcValue) {
  if (!rpcValue) {
    return undefined;
  }
  return wrapValue({
    name: rpcValue.name,
    previewType: rpcValue.previewType
  });
}
function transformUnifiedEvents(rpcValue) {
  if (!rpcValue || rpcValue.length === 0) {
    return undefined;
  }
  return wrapValue(rpcValue.map(event => {
    var _event$propertiesToMa, _event$isContactPrima;
    return {
      name: event.name,
      eventAssetIdProperty: event.eventAssetIdProperty,
      eventAssetIdType: event.eventAssetIdType,
      crmObjectUniquePropertyName: event.crmObjectUniquePropertyName,
      requiredProperties: event.requiredProperties,
      interactionType: event.interactionType,
      propertiesToMatch: (_event$propertiesToMa = event.propertiesToMatch) === null || _event$propertiesToMa === void 0 ? void 0 : _event$propertiesToMa.map(match => ({
        propertyName: match.propertyName,
        propertyValue: match.propertyValue,
        propertyOperation: match.propertyOperation
      })),
      isContactPrimaryObject: (_event$isContactPrima = event.isContactPrimaryObject) !== null && _event$isContactPrima !== void 0 ? _event$isContactPrima : false,
      contactIdProperty: event.contactIdProperty
    };
  }));
}
function transformObjectFilters(rpcValue) {
  if (!rpcValue || rpcValue.length === 0) {
    return undefined;
  }
  return wrapValue(rpcValue.map(filter => ({
    operator: filter.operator,
    property: filter.property,
    value: filter.value,
    values: filter.values
  })));
}
function transformTransitiveAssociations(rpcValue) {
  if (!rpcValue || rpcValue.length === 0) {
    return undefined;
  }
  return wrapValue(rpcValue.map(assoc => ({
    name: assoc.name,
    enabled: assoc.enabled,
    targetAssociationType: assoc.targetAssociationType,
    pathDefinition: assoc.pathDefinition
  })));
}
function transformCounters(rpcValue) {
  if (!rpcValue || rpcValue.length === 0) {
    return undefined;
  }
  return wrapValue(rpcValue.map(counter => ({
    name: counter.name,
    label: counter.label,
    metricRanking: counter.metricRanking,
    format: counter.format
  })));
}
function transformCrmPropertyCounters(rpcValue) {
  if (!rpcValue || rpcValue.length === 0) {
    return undefined;
  }
  return wrapValue(rpcValue.map(counter => ({
    propertyName: counter.propertyName,
    operation: counter.operation,
    label: counter.label,
    type: counter.type,
    metricRanking: counter.metricRanking
  })));
}
function transformTopMetricOverridePerPropertyValue(rpcValue) {
  if (!rpcValue) {
    return undefined;
  }
  return wrapValue({
    propertyName: rpcValue.propertyName,
    options: (rpcValue.options || []).map(option => ({
      propertyValue: option.propertyValue,
      topMetric: option.topMetric
    }))
  });
}
function transformBreakdownProperty(rpcValue) {
  if (!rpcValue) {
    return undefined;
  }
  return wrapValue({
    breakdownIdType: rpcValue.breakdownIdType,
    eventPropertyName: rpcValue.eventPropertyName,
    objectPropertyName: rpcValue.objectPropertyName
  });
}
function transformUpgradeKeys(rpcValue) {
  if (!rpcValue) {
    return undefined;
  }
  return wrapValue({
    addAssetsModal: rpcValue.addAssetsModal,
    assetRecommendation: rpcValue.assetRecommendation,
    studioAssetNode: rpcValue.studioAssetNode
  });
}
function transformRpcMetadataToFAS(objectTypeId, rpcMetadata) {
  var _rpcMetadata$enabledI, _rpcMetadata$enabledI2, _rpcMetadata$enabled;
  return {
    objectTypeId: wrapValue(objectTypeId),
    associationId: wrapValue(rpcMetadata.associationId),
    associationEnumValue: wrapValue(rpcMetadata.associationEnumValue),
    inverseAssociationId: wrapValue(rpcMetadata.inverseAssociationId),
    inverseAssociationEnumValue: wrapValue(rpcMetadata.inverseAssociationEnumValue),
    icon: wrapValue(rpcMetadata.icon),
    assetNameWithVariant: transformAssetNameWithVariant(rpcMetadata.assetNameWithVariant),
    enabledInCanvasView: wrapValue((_rpcMetadata$enabledI = rpcMetadata.enabledInCanvasView) !== null && _rpcMetadata$enabledI !== void 0 ? _rpcMetadata$enabledI : false),
    enabledInAddAssetDrawer: wrapValue((_rpcMetadata$enabledI2 = rpcMetadata.enabledInAddAssetDrawer) !== null && _rpcMetadata$enabledI2 !== void 0 ? _rpcMetadata$enabledI2 : false),
    studioGroupKey: wrapValue(rpcMetadata.studioGroupKey),
    enabled: wrapValue((_rpcMetadata$enabled = rpcMetadata.enabled) !== null && _rpcMetadata$enabled !== void 0 ? _rpcMetadata$enabled : true),
    userReadScope: wrapOptionalValue(rpcMetadata.userReadScope),
    userWriteScope: wrapOptionalValue(rpcMetadata.userWriteScope),
    userDraftWriteScope: wrapOptionalValue(rpcMetadata.userDraftWriteScope),
    requiredGates: wrapOptionalValue(rpcMetadata.requiredGates),
    requiredPortalScopes: wrapOptionalValue(rpcMetadata.requiredPortalScopes),
    canEdit: wrapOptionalValue(rpcMetadata.canEdit),
    canCreate: wrapOptionalValue(rpcMetadata.canCreate),
    canEditProperty: wrapOptionalValue(rpcMetadata.canEditProperty),
    campaignsCountProperty: wrapOptionalValue(rpcMetadata.campaignsCountProperty),
    navGroup: wrapOptionalValue(rpcMetadata.navGroup),
    hasNewBadge: wrapOptionalValue(rpcMetadata.hasNewBadge),
    hasBetaBadge: wrapOptionalValue(rpcMetadata.hasBetaBadge),
    typeProperty: wrapOptionalValue(rpcMetadata.typeProperty),
    statusProperty: transformStatusProperty(rpcMetadata.statusProperty),
    previewProperty: transformPreviewProperty(rpcMetadata.previewProperty),
    businessUnitProperty: wrapOptionalValue(rpcMetadata.businessUnitProperty),
    approvalStatusProperty: wrapOptionalValue(rpcMetadata.approvalStatusProperty),
    createdByProperty: wrapOptionalValue(rpcMetadata.createdByProperty),
    assetIdProperty: wrapOptionalValue(rpcMetadata.assetIdProperty),
    publishDateProperty: wrapOptionalValue(rpcMetadata.publishDateProperty),
    publishedAtProperty: wrapOptionalValue(rpcMetadata.publishedAtProperty),
    supportsDateRangeFiltering: wrapOptionalValue(rpcMetadata.supportsDateRangeFiltering),
    hasPerformancePage: wrapOptionalValue(rpcMetadata.hasPerformancePage),
    crmPropertyCounters: transformCrmPropertyCounters(rpcMetadata.crmPropertyCounters),
    topMetricOverridePerPropertyValue: transformTopMetricOverridePerPropertyValue(rpcMetadata.topMetricOverridePerPropertyValue),
    extraCrmPropertiesForBESync: wrapOptionalValue(rpcMetadata.extraCrmPropertiesForBESync),
    createActionTooltip: wrapOptionalValue(rpcMetadata.createActionTooltip),
    priorityInGroup: wrapOptionalValue(rpcMetadata.priorityInGroup),
    upgradeKeys: transformUpgradeKeys(rpcMetadata.upgradeKeys),
    upgradeKey: wrapOptionalValue(rpcMetadata.upgradeKey),
    unifiedEvents: transformUnifiedEvents(rpcMetadata.unifiedEvents),
    objectFilters: transformObjectFilters(rpcMetadata.objectFilters),
    transitiveAssociations: transformTransitiveAssociations(rpcMetadata.transitiveAssociations),
    eventReportName: wrapOptionalValue(rpcMetadata.eventReportName),
    breakdownProperty: transformBreakdownProperty(rpcMetadata.breakdownProperty),
    counters: transformCounters(rpcMetadata.counters),
    usesManualFetcher: wrapOptionalValue(rpcMetadata.usesManualFetcher),
    isCustomObject: {
      source: RPC_SOURCE,
      isPortalSpecific: true,
      value: rpcMetadata.isCustomObject === true
    },
    stackKey: wrapOptionalValue(rpcMetadata.stackKey),
    stackPriority: wrapOptionalValue(rpcMetadata.stackPriority),
    stackProperty: wrapOptionalValue(rpcMetadata.stackProperty),
    editActionType: wrapOptionalValue(rpcMetadata.editActionType),
    omitted: wrapValue(false),
    omittedReason: wrapValue('')
  };
}
function transformRpcEntryToFAS(objectTypeId, rpcEntry) {
  var _rpcEntry$campaignsAs;
  if (!(rpcEntry !== null && rpcEntry !== void 0 && (_rpcEntry$campaignsAs = rpcEntry.campaignsAssociations) !== null && _rpcEntry$campaignsAs !== void 0 && _rpcEntry$campaignsAs.metadata)) {
    return undefined;
  }
  return {
    [CAMPAIGNS_ASSOCIATIONS_FAS_NAMESPACE]: {
      metadata: transformRpcMetadataToFAS(objectTypeId, rpcEntry.campaignsAssociations.metadata)
    }
  };
}
export function transformRpcResponseToFAS(rpcResponse) {
  const assetConfigurations = rpcResponse.assetConfigurations || {};
  const objectPropertyDefinitions = rpcResponse.objectPropertyDefinitions || [];
  const objectTypeIdToName = new Map(objectPropertyDefinitions.map(def => [def.objectTypeId, def.name]));
  const result = Object.entries(assetConfigurations).reduce((acc, [objectTypeId, entry]) => {
    const transformed = transformRpcEntryToFAS(objectTypeId, entry);
    if (transformed) {
      var _objectTypeIdToName$g;
      const assetName = (_objectTypeIdToName$g = objectTypeIdToName.get(objectTypeId)) !== null && _objectTypeIdToName$g !== void 0 ? _objectTypeIdToName$g : objectTypeId;
      acc[assetName] = transformed;
    }
    return acc;
  }, {});
  return Object.entries(result).reduce((acc, [assetType, assetSetting]) => {
    if (hasAssetGateAccess(result, assetType)) {
      acc[assetType] = assetSetting;
    }
    return acc;
  }, {});
}